import { Box, HStack, Text } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';

import type { ChatActivity } from '../../../hooks/data/useAgentChat';
import { MaterialIcon } from '../../ui/MaterialIcon';
import { agentIcon, agentLabel, toolIcon } from './agentActivityLabels';

const breathe = keyframes`
  0%, 100% { opacity: 0.45; }
  50% { opacity: 1; }
`;

interface BirdAgentActivityProps {
  readonly activities: ReadonlyArray<ChatActivity>;
  readonly currentAgent: string | null;
  /** The line when there is no activity or agent to name. */
  readonly idleLabel?: string;
}

function pickIcon(tool: string | undefined, currentAgent: string | null): string {
  if (tool) return toolIcon(tool);
  if (currentAgent) return agentIcon(currentAgent);
  return 'auto_awesome';
}

function pickCurrentLine(
  activities: ReadonlyArray<ChatActivity>,
  currentAgent: string | null,
  idleLabel: string,
): { label: string; tool?: string } {
  const active = [...activities].reverse().find((a) => a.status === 'active');
  if (active) return { label: active.label, tool: active.tool };
  const last = activities[activities.length - 1];
  if (last) return { label: last.label, tool: last.tool };
  if (currentAgent) return { label: agentLabel(currentAgent) };
  return { label: idleLabel };
}

/**
 * Live single-line indicator shown while the agent is streaming. Picks
 * the most-recent active tool, then most-recent completed activity, then
 * the current sub-agent label, then a default "Thinking".
 *
 * Renders nothing once the turn is complete — by design we don't show a
 * post-hoc activity log on completed messages.
 */
export function BirdAgentActivity({
  activities,
  currentAgent,
  idleLabel = 'Thinking',
}: BirdAgentActivityProps) {
  const current = pickCurrentLine(activities, currentAgent, idleLabel);
  const icon = pickIcon(current.tool, currentAgent);

  return (
    <HStack spacing={2} mb={3} py={2} aria-live="polite">
      <Box
        animation={`${breathe} 1.6s ease-in-out infinite`}
        sx={{
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
            opacity: 1,
          },
        }}
        display="flex"
        alignItems="center"
      >
        <MaterialIcon name={icon} boxSize={4} color="text.secondary" />
      </Box>
      <Text fontSize="sm" color="text.secondary">
        {current.label}
      </Text>
    </HStack>
  );
}
